import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SlidersHorizontal, X, BadgeCheck, Search, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import SaveSearchButton from "@/components/SaveSearchButton";
import AiSearchBar from "@/components/AiSearchBar";

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // "Did you mean…" suggestions populated only when the primary query
  // returns zero results. Cleared on every fresh /properties fetch.
  const [suggestions, setSuggestions] = useState({ city_suggestions: [], locality_suggestions: [] });

  const [city, setCity] = useState(params.get("city") || "");
  const [locality, setLocality] = useState(params.get("locality") || "");
  const [listingType, setListingType] = useState(params.get("listing_type") || "all");
  const [propertyType, setPropertyType] = useState(params.get("property_type") || "all");
  const [bhk, setBhk] = useState(params.get("bhk") || "any");
  const [furnishing, setFurnishing] = useState(params.get("furnishing") || "any");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [ownerVerified, setOwnerVerified] = useState(params.get("owner_verified") === "1");
  const [sortBy, setSortBy] = useState(params.get("sort") || "newest");
  // On mobile the filter panel is collapsed by default so listings show first.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const buildQuery = useCallback(() => {
    const p = {};
    if (city) p.city = city;
    if (locality) p.locality = locality;
    if (listingType !== "all") p.listing_type = listingType;
    if (propertyType !== "all") p.property_type = propertyType;
    if (bhk !== "any") p.bhk = bhk;
    if (furnishing !== "any") p.furnishing = furnishing;
    if (minPrice) p.min_price = minPrice;
    if (maxPrice) p.max_price = maxPrice;
    if (ownerVerified) p.owner_verified = "true";
    if (sortBy && sortBy !== "newest") p.sort = sortBy;
    return p;
  }, [city, locality, listingType, propertyType, bhk, furnishing, minPrice, maxPrice, ownerVerified, sortBy]);

  const load = useCallback(() => {
    setLoading(true);
    setSuggestions({ city_suggestions: [], locality_suggestions: [] });
    api.get("/properties", { params: buildQuery() })
      .then(({ data }) => {
        setItems(data);
        // Only ask for "Did you mean…" suggestions when the search returned
        // nothing AND the user actually typed a city / locality. Filter-only
        // zero-results (e.g. "under ₹15k" with wrong BHK) don't need spell fixes.
        if (data.length === 0 && (city || locality)) {
          const p = {};
          if (city) p.city = city;
          if (locality) p.locality = locality;
          api.get("/properties/suggest", { params: p })
            .then(({ data: s }) => setSuggestions(s))
            .catch(() => { /* suggestion failure is non-fatal — silent no-op */ });
        }
      })
      .finally(() => setLoading(false));
  }, [buildQuery, city, locality]);

  useEffect(() => { load(); }, [load]);

  const apply = () => {
    const p = new URLSearchParams();
    const q = buildQuery();
    Object.entries(q).forEach(([k, v]) => p.set(k === "owner_verified" ? "owner_verified" : k, k === "owner_verified" ? "1" : String(v)));
    setParams(p);
    load();
    setFiltersOpen(false);
  };

  const clear = () => {
    setCity(""); setLocality(""); setListingType("all"); setPropertyType("all");
    setBhk("any"); setFurnishing("any"); setMinPrice(""); setMaxPrice("");
    setOwnerVerified(false); setSortBy("newest");
    setParams(new URLSearchParams());
  };

  /**
   * applySuggestion — user clicked a "Did you mean X" chip.
   * Swaps their typo/alt-name for the canonical value, keeps every other
   * filter intact, updates the URL, and re-fetches. Only city OR locality
   * is passed depending on which the suggestion is for.
   */
  const applySuggestion = (kind, name) => {
    if (kind === "city") setCity(name); else setLocality(name);
    const p = new URLSearchParams(params);
    p.set(kind, name);
    setParams(p);
    // load() is triggered by the [buildQuery] dep in useEffect
  };

  const toggleFav = async (id) => {
    if (!user) { toast.error("Please log in to save properties"); return; }
    try {
      await api.post(`/favorites/${id}`);
      await refresh();
    } catch { toast.error("Could not update favorite"); }
  };

  const activeFilterCount =
    (city ? 1 : 0) + (locality ? 1 : 0) + (listingType !== "all" ? 1 : 0) +
    (propertyType !== "all" ? 1 : 0) + (bhk !== "any" ? 1 : 0) +
    (furnishing !== "any" ? 1 : 0) + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) +
    (ownerVerified ? 1 : 0) + (sortBy !== "newest" ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <SEO
        title={
          city
            ? `${listingType === "sale" ? "Buy" : "Rent"} ${bhk !== "any" ? bhk + " BHK " : ""}homes in ${city}`
            : `${listingType === "sale" ? "Buy" : "Rent"} homes across India — Zero brokerage`
        }
        description={`Browse verified ${listingType === "sale" ? "sale" : "rent"} listings ${city ? `in ${city}` : "across India"}. Direct from owners. Zero brokerage. Filter by BHK, price, furnishing and more.`}
        path={`/properties${city ? `?city=${encodeURIComponent(city)}` : ""}`}
        keywords={`${city || "India"} rental listings, no brokerage rent, ${city || "India"} flats, owner rentals ${city || "India"}, 2BHK ${city || "India"}, apartments for rent`}
      />
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow text-gray-500 mb-2">Browse</p>
          <h1 className="font-display text-4xl sm:text-5xl brand-forest">Properties in your area</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500" data-testid="results-count">{items.length} results</span>
          <SaveSearchButton filters={buildQuery()} />
        </div>
      </div>

      {/* AI search entry — sits above the filters, never replaces them */}
      <div className="bg-gradient-to-br from-[#FFFBEE] to-white rounded-2xl border border-[#EFE3D0] p-5 mb-6">
        <p className="text-sm font-semibold brand-forest mb-3 inline-flex items-center gap-1.5">
          <Sparkles size={15} className="text-[#C9A24A]" /> Not sure how to filter? Just describe it.
        </p>
        <AiSearchBar variant="inline" />
      </div>

      {/* Filter panel */}
      <div className="bg-white rounded-2xl border border-brand-subtle p-5 mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="brand-forest" />
            <span className="label-eyebrow text-gray-600">Filters</span>
            {activeFilterCount > 0 && (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-forest text-white" data-testid="active-filter-count">
                {activeFilterCount} active
              </span>
            )}
          </div>
          {/* Mobile: toggle to reveal the full filter form (keeps listings above the fold) */}
          <button
            data-testid="mobile-filter-toggle"
            onClick={() => setFiltersOpen((o) => !o)}
            className="md:hidden inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand-forest text-white text-sm font-semibold active:scale-95 transition-transform"
          >
            <SlidersHorizontal size={14} /> {filtersOpen ? "Hide" : "Filters"}
          </button>
          {/* Desktop: Owner Verified toggle */}
          <label className="hidden md:inline-flex items-center gap-2 cursor-pointer select-none" data-testid="owner-verified-toggle">
            <BadgeCheck size={16} className={ownerVerified ? "text-brand-forest" : "text-gray-400"} />
            <span className={`text-sm font-medium ${ownerVerified ? "text-brand-forest" : "text-gray-600"}`}>
              Owner verified only
            </span>
            <Switch checked={ownerVerified} onCheckedChange={setOwnerVerified} data-testid="owner-verified-switch" />
          </label>
        </div>

        {/* Collapsible body — always open on desktop, toggled on mobile */}
        <div className={`${filtersOpen ? "block" : "hidden"} md:block`} data-testid="filter-body">
        {/* Mobile-only Owner Verified toggle */}
        <label className="md:hidden flex items-center justify-between gap-2 cursor-pointer select-none mb-3 px-1" data-testid="owner-verified-toggle-mobile">
          <span className="inline-flex items-center gap-2">
            <BadgeCheck size={16} className={ownerVerified ? "text-brand-forest" : "text-gray-400"} />
            <span className={`text-sm font-medium ${ownerVerified ? "text-brand-forest" : "text-gray-600"}`}>Owner verified only</span>
          </span>
          <Switch checked={ownerVerified} onCheckedChange={setOwnerVerified} data-testid="owner-verified-switch-mobile" />
        </label>

        {/* Row 1 – location & type */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-3">
          <Input data-testid="filter-city" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
          <Input data-testid="filter-locality" placeholder="Locality" value={locality} onChange={(e) => setLocality(e.target.value)} />
          <Select value={listingType} onValueChange={setListingType}>
            <SelectTrigger data-testid="filter-listing-type"><SelectValue placeholder="Rent/Sale" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Rent & Sale</SelectItem>
              <SelectItem value="rent">Rent</SelectItem>
              <SelectItem value="sale">Sale</SelectItem>
            </SelectContent>
          </Select>
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger data-testid="filter-property-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any type</SelectItem>
              <SelectItem value="apartment">Apartment</SelectItem>
              <SelectItem value="villa">Villa</SelectItem>
              <SelectItem value="house">Independent House</SelectItem>
              <SelectItem value="pg">PG / Roommates</SelectItem>
              <SelectItem value="plot">Plot / Land</SelectItem>
            </SelectContent>
          </Select>
          <Select value={bhk} onValueChange={setBhk}>
            <SelectTrigger data-testid="filter-bhk"><SelectValue placeholder="BHK" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any BHK</SelectItem>
              <SelectItem value="1">1 BHK</SelectItem>
              <SelectItem value="2">2 BHK</SelectItem>
              <SelectItem value="3">3 BHK</SelectItem>
              <SelectItem value="4">4+ BHK</SelectItem>
            </SelectContent>
          </Select>
          <Select value={furnishing} onValueChange={setFurnishing}>
            <SelectTrigger data-testid="filter-furnishing"><SelectValue placeholder="Furnishing" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any furnishing</SelectItem>
              <SelectItem value="fully">Fully furnished</SelectItem>
              <SelectItem value="semi">Semi furnished</SelectItem>
              <SelectItem value="unfurnished">Unfurnished</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Row 2 – price range presets */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input data-testid="filter-min-price" placeholder="Min ₹" type="number" inputMode="numeric" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
          <Input data-testid="filter-max-price" placeholder="Max ₹" type="number" inputMode="numeric" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
          <div className="col-span-2 flex flex-wrap items-center gap-1.5" data-testid="price-presets">
            {[
              { l: "Under ₹15k", min: "", max: "15000" },
              { l: "₹15k – ₹30k", min: "15000", max: "30000" },
              { l: "₹30k – ₹60k", min: "30000", max: "60000" },
              { l: "₹60k+", min: "60000", max: "" },
            ].map((p) => (
              <button
                key={p.l}
                type="button"
                onClick={() => { setMinPrice(p.min); setMaxPrice(p.max); }}
                data-testid={`price-preset-${p.l.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-xs px-3 py-1.5 rounded-full border border-brand-subtle text-gray-700 hover:bg-brand-subtle/30 transition-colors"
              >
                {p.l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button data-testid="apply-filters" onClick={apply} className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-10 px-6">Apply filters</Button>
          <Button data-testid="clear-filters" onClick={clear} variant="ghost" className="rounded-full h-10 px-4">
            <X size={14} className="mr-1" /> Clear
          </Button>
        </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Loading properties…</div>
      ) : items.length === 0 ? (
        <div className="py-16 bg-white rounded-2xl border border-brand-subtle" data-testid="empty-state">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-14 h-14 rounded-full bg-[#FBF6EC] flex items-center justify-center mx-auto mb-4">
              <Search size={22} className="text-[#7A1E2A]" />
            </div>
            <p className="font-display text-2xl brand-forest mb-2">No properties found</p>
            <p className="text-gray-500 text-sm mb-6">
              {city || locality
                ? "We couldn't find matching listings. Try one of these instead:"
                : "Try adjusting your filters or clearing them."}
            </p>

            {/* "Did you mean…" chips — fuzzy backend suggestions with counts */}
            {(suggestions.city_suggestions.length > 0 || suggestions.locality_suggestions.length > 0) && (
              <div data-testid="did-you-mean" className="mb-6">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Did you mean</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestions.city_suggestions.map((s) => (
                    <button
                      key={`city-${s.name}`}
                      data-testid={`suggest-city-${s.name}`}
                      onClick={() => applySuggestion("city", s.name)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7A1E2A] text-white text-sm font-semibold hover:bg-[#5C1420] active:scale-95 transition-all"
                    >
                      {s.name}
                      <span className="text-[10px] font-normal opacity-80 bg-white/20 px-1.5 py-0.5 rounded-full">
                        {s.count}
                      </span>
                    </button>
                  ))}
                  {suggestions.locality_suggestions.map((s) => (
                    <button
                      key={`loc-${s.name}`}
                      data-testid={`suggest-locality-${s.name}`}
                      onClick={() => applySuggestion("locality", s.name)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#1B0F14] text-sm font-semibold border border-[#C9A24A] hover:bg-[#FBF6EC] active:scale-95 transition-all"
                    >
                      {s.name}
                      <span className="text-[10px] font-normal opacity-70 bg-[#7A1E2A]/10 text-[#7A1E2A] px-1.5 py-0.5 rounded-full">
                        {s.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-3">
              <Button
                data-testid="empty-state-clear"
                variant="outline"
                onClick={clear}
                className="rounded-full h-10 px-5 border-[#EFE3D0] text-[#7A1E2A] hover:bg-[#FBF6EC]"
              >
                Clear all filters
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              isFav={user && user.favorites?.includes(p.id)}
              onToggleFav={toggleFav}
            />
          ))}
        </div>
      )}
    </div>
  );
}
